import React, { useState } from "react";
import { Col, Container, Row } from "reactstrap";
import Widget from "./Widgets";
import WidgetsV from "./WidgetsV";
import WidgetsE from "./WidgetsE";
import BestSellingProducts from "./BestSellingProducts";
import RecentOrders from "./RecentOrders";
import RecentExpedition from "./RecentExpedition";
import Revenue from "./Revenue";
import Section from "./Section";
import StoreVisits from "./StoreVisits";
import TopSellers from "./TopSellers";
import { useAuth } from '../../../Provider/AuthProvider';
import StockAlerts from "./StockAlerts";

interface DateFilter {
    type: 'period';
    start: Date;
    end: Date;
}

const DashboardEcommerce = () => {
  document.title = "Dashboard | APA";

  const [rightColumn, setRightColumn] = useState<boolean>(false);
  const { user, allowedInterfaces } = useAuth();
  
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    type: 'period',
    start: new Date(new Date().getFullYear(), 0, 1),
    end: new Date()
  });
  
  const toggleRightColumn = () => {
    setRightColumn(!rightColumn);
  };

  const handleApplyFilter = (startDate: Date, endDate: Date) => {
    console.log('✅ Filtre appliqué dans Dashboard:', { startDate, endDate });
    setDateFilter({
      type: 'period',
      start: startDate,
      end: endDate
    });
  };

  const userinfo = user?.userinfo;
  const isAdmin = userinfo?.admin === "1";
  const isEmployee = userinfo?.admin === "0" && userinfo?.employee === "1";
  const hasVendeurAccess = isEmployee && allowedInterfaces.includes("vendeur");
  const hasStockAccess = isEmployee && allowedInterfaces.includes("stock");

  const getWidgetComponent = () => {
    if (!userinfo) return null;
    
    if (isAdmin) return <Widget dateFilter={dateFilter} />;
    
    if (isEmployee) {
      if (hasVendeurAccess) {
        return <WidgetsV dateFilter={dateFilter} />;
      }
      
      if (hasStockAccess) {
        return <WidgetsE dateFilter={dateFilter} />;
      }
    }
    
    return null;
  };

  const getSecondaryComponent = () => {
    if (!userinfo) return null;
    
    if (isAdmin) return <TopSellers />;
    
    if (isEmployee) {
      if (hasVendeurAccess) {
        return <TopSellers />;
      }
      
      if (hasStockAccess) {
        return null; // Plus de composant secondaire, on affiche StockAlerts en pleine largeur
      }
    }
    
    return null;
  };

  const getOrdersOrExpeditionComponent = () => {
    if (!userinfo) return null;
    
    if (isAdmin) return <RecentOrders />;
    
    if (isEmployee) {
      if (hasVendeurAccess) {
        return <RecentOrders />;
      }
      
      if (hasStockAccess) {
        return <RecentExpedition />;
      }
    }
    
    return null;
  };

  // Déterminer si on doit afficher BestSellingProducts
  const showBestSellingProducts = () => {
    if (!userinfo) return false;
    if (isAdmin) return true;
    if (isEmployee && hasVendeurAccess) return true;
    return false; // hasStockAccess retourne false
  };

  // Déterminer si on doit afficher StockAlerts en pleine largeur
  const showFullWidthStockAlerts = () => {
    return isEmployee && hasStockAccess;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <div className="h-100">
              <Section 
                rightClickBtn={toggleRightColumn}
                onApplyFilter={handleApplyFilter}
              />
              
              {/* Widgets section avec filtre */}
              <Row>
                {getWidgetComponent()}
              </Row>
              
              {/* Products and Secondary component section */}
              {showBestSellingProducts() && (
                <Row>
                  <BestSellingProducts />
                  {getSecondaryComponent()}
                </Row>
              )}
              
              {/* StockAlerts en pleine largeur pour hasStockAccess */}
              {showFullWidthStockAlerts() && (
                <Row>
                  <Col lg={12}>
                    <StockAlerts />
                  </Col>
                </Row>
              )}
              
              {/* Revenue section - seulement pour admin */}
              {isAdmin && (
                <Row>
                  <Revenue />
                </Row>
              )}
              
              {/* Section RecentOrders ou RecentExpedition en pleine largeur */}
              <Row>
                {getOrdersOrExpeditionComponent()}
                <StoreVisits />
              </Row>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DashboardEcommerce;